/**
 * CSV Schema Sniffer - Automatisk detektion av kolumntyper
 * 
 * Detekterar:
 * - Datum: ≥80% träff på datumregex
 * - Belopp: ≥90% numeriska, innehåller både + och −
 * - Saldo: uppfyller saldo[i] ≈ saldo[i-1] + belopp[i]
 * - Händelse: mest textuell, innehåller nyckelord
 */

export interface ColumnAnalysis {
  index: number;
  type: 'date' | 'amount' | 'balance' | 'event' | 'unknown';
  confidence: number;
  samples: string[];
  metadata?: {
    dateFormat?: string;
    hasNegatives?: boolean;
    hasPositives?: boolean;
    avgLength?: number;
    keywords?: string[];
    balanceConsistency?: number;
  };
}

export interface SchemaInferenceResult {
  schema: {
    date_idx: number | null;
    event_idx: number | null;
    amount_idx: number | null;
    balance_idx: number | null;
  };
  columns: ColumnAnalysis[];
  confidence: number;
  warnings: string[];
}

const DATE_PATTERNS = [
  /^\d{4}-\d{2}-\d{2}$/, // YYYY-MM-DD
  /^\d{8}$/, // YYYYMMDD
  /^\d{2}\/\d{2}\/\d{4}$/, // DD/MM/YYYY
  /^\d{2}\.\d{2}\.\d{4}$/, // DD.MM.YYYY
  /^\d{2}-\d{2}-\d{4}$/, // DD-MM-YYYY
  /^\d{4}\d{2}\d{2}$/ // YYYYMMDD (no separators)
];

const EVENT_KEYWORDS = [
  'intäktsränta', 'slutlig skatt', 'moms', 'arbetsgivaravgift', 'preliminärskatt',
  'f-skatt', 'skatt', 'avgift', 'debitering', 'preliminär', 'restskatt', 'överskott',
  'inbetalning', 'utbetalning', 'ränta', 'överföring', 'återbetalning'
];

/**
 * Normalisera svenskt tal: ta bort mellanslag/NBSP, byt , → ., hantera minus
 */
function normalizeSwedishNumber(value: string): number | null {
  if (!value || typeof value !== 'string') return null;
  
  let s = value.trim();

  // Negativa tal i parentes: (1000) -> -1000
  const paren = s.match(/^\(([^)]+)\)$/);
  if (paren) s = '-' + paren[1].trim();

  // Ta bort valutor/suffix
  s = s.replace(/\s*(SEK|kr|:-|€|EUR|USD|GBP)\s*$/gi, '');

  // Normalisera minustecken (unicode → '-')
  s = s.replace(/[\u2212−–—]/g, '-');

  // Ta bort alla whitespace-varianter
  s = s.replace(/[\s\u00A0\u2009\u202F]+/g, '');

  // Ta bort apostrof som tusentalsavskiljare
  s = s.replace(/'/g, '');

  // Hantera blandade tusentals-/decimaltecken
  const dotCount = (s.match(/\./g) || []).length;
  const commaCount = (s.match(/,/g) || []).length;

  if (commaCount > 0 && dotCount > 0) {
    // T.ex. 1.234,56 -> 1234.56
    s = s.replace(/\./g, '').replace(',', '.');
  } else if (commaCount > 0) {
    // T.ex. 1234,56 -> 1234.56
    s = s.replace(',', '.');
  } else if (dotCount > 1) {
    // T.ex. 1.234.567.89 -> behåll sista punkt som decimal, ta bort övriga
    const last = s.lastIndexOf('.');
    s = s.slice(0, last).replace(/\./g, '') + '.' + s.slice(last + 1);
  }

  const num = parseFloat(s);
  if (!isNaN(num)) return num;

  // Fallback: extrahera första talmönster och normalisera igen
  const m = value.match(/-?\(?[\d\s\u00A0\u2009\u202F.,']+(?:[.,]\d+)?\)?/);
  if (m) {
    let t = m[0]
      .replace(/[\u2212−–—]/g, '-')
      .replace(/[\s\u00A0\u2009\u202F']/g, '');
    if (t.startsWith('(') && t.endsWith(')')) t = '-' + t.slice(1, -1);

    const d = (t.match(/\./g) || []).length;
    const c = (t.match(/,/g) || []).length;
    if (c > 0 && d > 0) t = t.replace(/\./g, '').replace(',', '.');
    else if (c > 0) t = t.replace(',', '.');
    else if (d > 1) {
      const last = t.lastIndexOf('.');
      t = t.slice(0, last).replace(/\./g, '') + '.' + t.slice(last + 1);
    }

    const parsed = parseFloat(t);
    if (!isNaN(parsed)) return parsed;
  }

  return null;
}

/**
 * Kontrollera om en sträng är ett datum
 */
function isLikelyDate(value: string): boolean {
  if (!value || typeof value !== 'string') return false;
  
  let trimmed = value.trim();

  // Strip time component if present (e.g., YYYY-MM-DD HH:MM:SS or ISO with T)
  if (/^\d{4}-\d{2}-\d{2}[ T]/.test(trimmed)) {
    trimmed = trimmed.split(/[ T]/)[0];
  }
  
  // Excel serienummer (40000-50000 range för 2010-2030)
  const asNumber = parseFloat(trimmed);
  if (!isNaN(asNumber) && asNumber >= 40000 && asNumber <= 50000) {
    return true;
  }
  
  // Regex-mönster
  return DATE_PATTERNS.some(pattern => pattern.test(trimmed));
}

/**
 * Kontrollera om en sträng är numerisk
 */
function isNumeric(value: string): boolean {
  return normalizeSwedishNumber(value) !== null;
}

/**
 * Analysera en enskild kolumn
 */
function analyzeColumn(values: string[], columnIndex: number): ColumnAnalysis {
  const nonEmpty = values.filter(v => v && v.trim());
  if (nonEmpty.length === 0) {
    return {
      index: columnIndex,
      type: 'unknown',
      confidence: 0,
      samples: []
    };
  }
  
  const samples = nonEmpty.slice(0, 5);
  
  // Datum-detektion
  const dateMatches = nonEmpty.filter(isLikelyDate).length;
  const dateCoverage = dateMatches / nonEmpty.length;
  
  if (dateCoverage >= 0.8) {
    return {
      index: columnIndex,
      type: 'date',
      confidence: dateCoverage,
      samples,
      metadata: { dateFormat: 'mixed' }
    };
  }
  
  // Numerisk detektion
  const numericValues = nonEmpty.map(normalizeSwedishNumber).filter(n => n !== null) as number[];
  const numericCoverage = numericValues.length / nonEmpty.length;
  
  if (numericCoverage >= 0.9) {
    const hasNegatives = numericValues.some(n => n < 0);
    const hasPositives = numericValues.some(n => n > 0);
    
    // Belopp: både + och −
    if (hasNegatives && hasPositives) {
      return {
        index: columnIndex,
        type: 'amount',
        confidence: numericCoverage,
        samples,
        metadata: { hasNegatives, hasPositives }
      };
    }
    
    // Saldo: endast positiva eller ackumulerande
    return {
      index: columnIndex,
      type: 'balance',
      confidence: numericCoverage * 0.8, // Lower confidence för saldo
      samples,
      metadata: { hasNegatives, hasPositives }
    };
  }
  
  // Text-detektion (händelse)
  const lowerValues = nonEmpty.map(v => v.toLowerCase());
  const keywordMatches = lowerValues.filter(v => 
    EVENT_KEYWORDS.some(kw => v.includes(kw))
  ).length;
  const keywordCoverage = keywordMatches / nonEmpty.length;
  
  if (keywordCoverage >= 0.3) {
    return {
      index: columnIndex,
      type: 'event',
      confidence: 0.7 + (keywordCoverage * 0.3),
      samples,
      metadata: {
        avgLength: nonEmpty.reduce((sum, v) => sum + v.length, 0) / nonEmpty.length,
        keywords: EVENT_KEYWORDS.filter(kw => lowerValues.some(v => v.includes(kw)))
      }
    };
  }
  
  // Fallback: text-kolumn
  const avgLength = nonEmpty.reduce((sum, v) => sum + v.length, 0) / nonEmpty.length;
  if (avgLength > 10) {
    return {
      index: columnIndex,
      type: 'event',
      confidence: 0.5,
      samples,
      metadata: { avgLength }
    };
  }
  
  return {
    index: columnIndex,
    type: 'unknown',
    confidence: 0,
    samples
  };
}

/**
 * Validera saldo-konsistens: saldo[i] ≈ saldo[i-1] + belopp[i]
 */
function validateBalanceConsistency(
  balanceValues: (number | null)[],
  amountValues: (number | null)[]
): number {
  let matches = 0;
  let total = 0;
  
  for (let i = 1; i < Math.min(balanceValues.length, amountValues.length); i++) {
    const prevBalance = balanceValues[i - 1];
    const currentBalance = balanceValues[i];
    const amount = amountValues[i];
    
    if (prevBalance !== null && currentBalance !== null && amount !== null) {
      const expected = prevBalance + amount;
      const diff = Math.abs(currentBalance - expected);
      
      if (diff < 0.01) {
        matches++;
      }
      total++;
    }
  }
  
  return total > 0 ? matches / total : 0;
}

/**
 * Inferera schema från CSV-rader
 */
export function inferSchema(
  rows: string[][],
  sampleSize: number = 200
): SchemaInferenceResult {
  const warnings: string[] = [];
  
  if (rows.length === 0) {
    return {
      schema: { date_idx: null, event_idx: null, amount_idx: null, balance_idx: null },
      columns: [],
      confidence: 0,
      warnings: ['Tom CSV-fil']
    };
  }
  
  const numColumns = Math.max(...rows.map(r => r.length));
  const sampleRows = rows.slice(0, Math.min(sampleSize, rows.length));
  
  // Analysera varje kolumn
  const columns: ColumnAnalysis[] = [];
  for (let colIdx = 0; colIdx < numColumns; colIdx++) {
    const values = sampleRows.map(row => row[colIdx] || '');
    const analysis = analyzeColumn(values, colIdx);
    columns.push(analysis);
  }
  
  // Välj bästa kandidater för varje typ
  const dateCandidates = columns.filter(c => c.type === 'date').sort((a, b) => b.confidence - a.confidence);
  const amountCandidates = columns.filter(c => c.type === 'amount').sort((a, b) => b.confidence - a.confidence);
  const balanceCandidates = columns.filter(c => c.type === 'balance').sort((a, b) => b.confidence - a.confidence);
  const eventCandidates = columns.filter(c => c.type === 'event').sort((a, b) => b.confidence - a.confidence);
  
  let date_idx = dateCandidates[0]?.index ?? null;
  let amount_idx = amountCandidates[0]?.index ?? null;
  let balance_idx = balanceCandidates[0]?.index ?? null;
  let event_idx = eventCandidates[0]?.index ?? null;
  
  // Validera saldo-konsistens om vi har både belopp och saldo
  if (amount_idx !== null && balance_idx !== null) {
    const amountValues = sampleRows.map(row => normalizeSwedishNumber(row[amount_idx!]));
    const balanceValues = sampleRows.map(row => normalizeSwedishNumber(row[balance_idx!]));
    
    const consistency = validateBalanceConsistency(balanceValues, amountValues);
    
    if (consistency < 0.8) {
      warnings.push(`Saldo-konsistens låg (${Math.round(consistency * 100)}%). Kontrollera att rätt kolumner är valda.`);
      
      // Om konsistensen är för låg, försök hitta en bättre saldo-kolumn
      for (const candidate of balanceCandidates.slice(1)) {
        const candidateValues = sampleRows.map(row => normalizeSwedishNumber(row[candidate.index]));
        const candidateConsistency = validateBalanceConsistency(candidateValues, amountValues);
        
        if (candidateConsistency > consistency) {
          balance_idx = candidate.index;
          warnings.push(`Bytte till saldo-kolumn ${balance_idx + 1} (bättre konsistens: ${Math.round(candidateConsistency * 100)}%)`);
          break;
        }
      }
    }
  }
  
  // Beräkna övergripande konfidensgrad
  const requiredFields = [date_idx, amount_idx, event_idx].filter(idx => idx !== null);
  const avgConfidence = requiredFields.length > 0
    ? requiredFields.reduce((sum, idx) => {
        const col = columns.find(c => c.index === idx);
        return sum + (col?.confidence || 0);
      }, 0) / requiredFields.length
    : 0;
  
  // Varningar
  if (date_idx === null) warnings.push('Datum-kolumn kunde inte detekteras');
  if (amount_idx === null) warnings.push('Belopp-kolumn kunde inte detekteras');
  if (event_idx === null) warnings.push('Händelse-kolumn kunde inte detekteras');
  
  // Kontrollera att Saldo inte används som Datum
  if (date_idx !== null && balance_idx !== null && date_idx === balance_idx) {
    warnings.push('⚠️ KRITISK: Saldo-kolumn detekterad som Datum! Korrigerar...');
    
    // Hitta nästa bästa datum-kandidat
    const nextDateCandidate = dateCandidates.find(c => c.index !== balance_idx);
    if (nextDateCandidate) {
      date_idx = nextDateCandidate.index;
      warnings.push(`Bytte till datum-kolumn ${date_idx + 1}`);
    } else {
      date_idx = null;
      warnings.push('Kunde inte hitta alternativ datum-kolumn');
    }
  }
  
  return {
    schema: {
      date_idx,
      event_idx,
      amount_idx,
      balance_idx
    },
    columns,
    confidence: avgConfidence,
    warnings
  };
}

/**
 * Konvertera "Kolumn N" till index (0-baserad)
 */
export function parseColumnName(columnName: string): number | null {
  const match = columnName.match(/^Kolumn (\d+)$/i);
  if (match) {
    return parseInt(match[1], 10) - 1; // Konvertera till 0-baserat index
  }
  return null;
}
